/**
 * Live caption translation — browser-direct Gemini call on the user's own key.
 *
 * Reuses the Quiet-Ask browser-direct path (ADR 0004): the ONLY browser-direct
 * LLM calls in the app. Each finalized transcript line is translated once into
 * the caption target language and cached, so re-renders never re-translate.
 */

import { langMeta } from "@scoach/types";
import type { LangCode } from "@scoach/types";

import { effectiveGeminiKey } from "../../../../lib/geminiKey.ts";
import { callGemini, textOf } from "./quietAsk.ts";

// Cache keyed by `${target}\n${sourceText}` → translated text. Kept for the life
// of the tab; transcript lines are short so memory is a non-issue.
const cache = new Map<string, string>();

function cacheKey(target: LangCode, text: string): string {
  return `${target}\n${text}`;
}

/** Synchronously read an already-translated line, if present. */
export function cachedTranslation(target: LangCode, text: string): string | undefined {
  return cache.get(cacheKey(target, text));
}

/**
 * Translate a single transcript line into `target`. Successful translations are
 * memoized per (target, text). THROWS on failure (no silent fall-back to the
 * original) so the captions panel can show why nothing is translating, and so a
 * transient error isn't cached and can be retried on the next tick.
 */
export async function translateLine(target: LangCode, text: string): Promise<string> {
  const clean = text.trim();
  if (!clean) return "";
  const key = cacheKey(target, clean);
  const hit = cache.get(key);
  if (hit !== undefined) return hit;

  const apiKey = effectiveGeminiKey();
  if (!apiKey) throw new Error("No Gemini API key set. Add one in Settings.");

  const targetName = langMeta(target).labelEn;
  const body = {
    systemInstruction: {
      role: "system",
      parts: [
        {
          text: `You are a live-caption translator. Translate the user's text into ${targetName}. Output ONLY the translation — no quotes, no notes, no preamble. If the text is already in ${targetName}, return it unchanged. Preserve product names and proper nouns.`,
        },
      ],
    },
    contents: [{ role: "user", parts: [{ text: clean }] }],
    generationConfig: { temperature: 0, maxOutputTokens: 512 },
  };

  const json = await callGemini(apiKey, body);
  const out = textOf(json).trim();
  const result = out || clean;
  cache.set(key, result);
  return result;
}
